/**
 * PWA install detection + instructional prompt state.
 *
 * iOS Safari never fires `beforeinstallprompt`; users install PWAs
 * manually via the share sheet → "Add to Home Screen". We detect the
 * platform and surface a one-time instructional modal instead of a
 * disabled button.
 *
 * Android / Chrome fires `beforeinstallprompt`, so we capture it and
 * expose a `promptInstall()` helper.
 */

const DISMISSED_KEY = 'scanpay.install-prompt.dismissedAt';
const DEFAULT_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000; // re-ask after 7 days

export type Platform = 'ios' | 'android-chrome' | 'desktop' | 'other';

export interface PlatformInfo {
  platform: Platform;
  isStandalone: boolean;
  canInstallProgrammatically: boolean;
}

export function detectPlatform(userAgent: string, standaloneHint?: boolean): PlatformInfo {
  const ua = userAgent.toLowerCase();
  const isIOS = /iphone|ipad|ipod/.test(ua);
  // iPadOS 13+ lies about being macOS — detect via touch support heuristic if
  // userAgent alone is ambiguous.
  const isAndroidChrome = /android/.test(ua) && /chrome/.test(ua);
  const isDesktop = !isIOS && !isAndroidChrome && !/mobi|android|iphone/.test(ua);

  const standalone = standaloneHint ?? detectStandalone();

  let platform: Platform = 'other';
  if (isIOS) platform = 'ios';
  else if (isAndroidChrome) platform = 'android-chrome';
  else if (isDesktop) platform = 'desktop';

  return {
    platform,
    isStandalone: standalone,
    canInstallProgrammatically: platform === 'android-chrome' || platform === 'desktop',
  };
}

export function detectStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  if (window.matchMedia?.('(display-mode: standalone)').matches) return true;
  if ((window.navigator as { standalone?: boolean }).standalone === true) return true;
  return false;
}

/**
 * Check whether the install prompt should be shown now.
 *   - Never when already installed (standalone mode).
 *   - Never if dismissed within the cooldown window.
 */
export function shouldShowInstallPrompt(info: PlatformInfo, cooldownMs = DEFAULT_COOLDOWN_MS): boolean {
  if (info.isStandalone) return false;
  if (info.platform === 'other') return false;
  if (typeof window === 'undefined') return false;
  const dismissedAtRaw = window.localStorage?.getItem(DISMISSED_KEY);
  if (!dismissedAtRaw) return true;
  const dismissedAt = Number(dismissedAtRaw);
  if (!Number.isFinite(dismissedAt)) return true;
  return Date.now() - dismissedAt >= cooldownMs;
}

export function markInstallPromptDismissed(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.setItem(DISMISSED_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable — we'll just re-prompt next session.
  }
}

export function clearInstallPromptDismissal(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage?.removeItem(DISMISSED_KEY);
  } catch {
    // no-op
  }
}

/**
 * Localised iOS install instructions — matches the manual "Add to Home
 * Screen" flow (share button → Add to Home Screen).
 */
export const IOS_INSTRUCTIONS = [
  'Tap the Share button in Safari (square with an arrow).',
  'Scroll down and tap "Add to Home Screen".',
  'Tap "Add" in the top-right corner.',
] as const;

// Android / desktop: BeforeInstallPromptEvent capture helper ─────────────

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
};

let capturedPrompt: BeforeInstallPromptEvent | null = null;

export function captureBeforeInstallPrompt(): () => void {
  if (typeof window === 'undefined') return () => {};
  const handler = (event: Event) => {
    event.preventDefault();
    capturedPrompt = event as BeforeInstallPromptEvent;
  };
  window.addEventListener('beforeinstallprompt', handler);
  return () => window.removeEventListener('beforeinstallprompt', handler);
}

export async function triggerNativeInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!capturedPrompt) return 'unavailable';
  try {
    await capturedPrompt.prompt();
    const { outcome } = await capturedPrompt.userChoice;
    capturedPrompt = null;
    return outcome;
  } catch {
    return 'dismissed';
  }
}
